import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  checkCurrentSession,
  fetchAuthMode,
  getCachedSession,
  signInDevOnly as signInDevOnlyRequest,
  signInWithPassword as signInWithPasswordRequest,
  signOut as signOutRequest,
  type AuthMode,
  type Session
} from './authClient';

interface AuthContextValue {
  session: Session | null;
  /** null while still being determined (see the mount effect below). */
  authMode: AuthMode | null;
  /** True only during the initial check with nothing cached to show yet
   * — once there's a session (real or cached) or a confirmed "signed
   * out", this is false and the UI never blocks on it again. */
  loading: boolean;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signInDevOnly: (name: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => getCachedSession());
  const [authMode, setAuthMode] = useState<AuthMode | null>(null);
  const [loading, setLoading] = useState(() => getCachedSession() === null);
  const ranOnce = useRef(false);

  useEffect(() => {
    if (ranOnce.current) return;
    ranOnce.current = true;

    (async () => {
      const mode = await fetchAuthMode();
      setAuthMode(mode);

      if (mode === 'unavailable') {
        // No backend to check with at all — trust whatever we already
        // have (a cached session, or nothing) and stop blocking the UI.
        setLoading(false);
        return;
      }

      const { session: fresh, reachable } = await checkCurrentSession();
      if (reachable) {
        // Authoritative answer from the server — either a real session
        // or a real "not signed in".
        setSession(fresh);
      }
      // Otherwise the config check above reached the server a moment ago
      // but this one didn't (a race, or the network just dropped) — keep
      // whatever was already showing rather than bouncing to sign-in.
      setLoading(false);
    })();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      authMode,
      loading,
      signInWithPassword: async (email, password) => {
        setSession(await signInWithPasswordRequest(email, password));
      },
      signInDevOnly: async name => {
        setSession(await signInDevOnlyRequest(name, { verifyWithServer: authMode === 'dev' }));
      },
      signOut: async () => {
        await signOutRequest();
        setSession(null);
      }
    }),
    [session, authMode, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
